import { useScrollReveal } from '../hooks/useScrollReveal';
import { useEffect, useState } from 'react';

const roadmapSteps = [
  {
    id: 1,
    title: '成果基础',
    desc: '六年评课数据积累与系统验证',
    icon: 'ri-database-2-line',
    stats: '6年 · 16.7万条',
    tag: '数据底座',
    time: '2019-2025',
  },
  {
    id: 2,
    title: '评价难点',
    desc: '思政课评价为什么难、难在哪里',
    icon: 'ri-error-warning-line',
    stats: '5大难点',
    tag: '问题拆解',
    time: '现状诊断',
  },
  {
    id: 3,
    title: 'AI评课原理',
    desc: 'AI理解课堂、识别证据、形成判断',
    icon: 'ri-brain-line',
    stats: '3大模块 · 7步流程',
    tag: '技术内核',
    time: '模型能力',
  },
  {
    id: 4,
    title: '案例演示',
    desc: '三个真实课堂的深度评课案例',
    icon: 'ri-file-list-3-line',
    stats: '3节课 · 5维度',
    tag: '实战验证',
    time: '效果展示',
  },
  {
    id: 5,
    title: '应用服务',
    desc: '教师·学生·专家·管理者四类角色',
    icon: 'ri-user-shared-line',
    stats: '4类角色 · 23项功能',
    tag: '多端服务',
    time: '落地应用',
  },
  {
    id: 6,
    title: '质量治理',
    desc: '一师一档·一校一图·一省一图三级治理',
    icon: 'ri-map-2-line',
    stats: '3级图谱 · 5门课程',
    tag: '体系治理',
    time: '规模化',
  },
  {
    id: 7,
    title: '总结与问答',
    desc: '从单课评价走向系统质量治理的完整链路',
    icon: 'ri-chat-quote-line',
    stats: '7大部分 · 82页',
    tag: '全景回顾',
    time: '收束闭环',
  },
];

function RoadLine({ activeStep }: { activeStep: number }) {
  const total = roadmapSteps.length;
  const progress = Math.max(0, Math.min(1, (activeStep + 1) / total));

  return (
    <div className="absolute bottom-[52px] left-[10%] right-[10%] h-1.5 rounded-full overflow-hidden">
      {/* 背景轨道 */}
      <div className="absolute inset-0 rounded-full bg-background-200/60" />
      {/* 金色进度条 */}
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary-400 via-accent-400 to-accent-500 transition-all duration-1000 ease-out"
        style={{ width: `${progress * 100}%` }}
      />
      {/* 节点圆点 */}
      <div className="absolute inset-0 flex items-center justify-between px-[2%]">
        {roadmapSteps.map((_, i) => {
          const isActive = activeStep >= i;
          return (
            <div
              key={i}
              className={`w-3 h-3 rounded-full border-2 transition-all duration-500 ${
                isActive
                  ? 'bg-white border-accent-400 scale-100'
                  : 'bg-background-200 border-background-300 scale-75'
              }`}
              style={{ transitionDelay: `${i * 120 + 300}ms` }}
            />
          );
        })}
      </div>
    </div>
  );
}

export default function Section7Roadmap({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const [activeStep, setActiveStep] = useState(-1);

  useEffect(() => {
    if (!titleVisible) { setActiveStep(-1); return; }
    let s = -1;
    const t = setInterval(() => {
      s += 1;
      setActiveStep(s);
      if (s >= roadmapSteps.length - 1) clearInterval(t);
    }, 220);
    return () => clearInterval(t);
  }, [titleVisible]);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 柔和背景层次 */}
      <div className="absolute inset-0 bg-gradient-to-b from-background-50 via-primary-50/20 to-accent-50/10 pointer-events-none" />
      <div className="absolute top-1/4 left-0 w-96 h-96 rounded-full bg-primary-100/6 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-0 w-80 h-80 rounded-full bg-accent-100/8 blur-[100px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-10 md:py-14">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-10 md:mb-14">
          <div
            className={`flex items-center justify-center gap-3 mb-4 transition-all duration-700 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="w-8 h-px bg-accent-300/60" />
            <span className="text-sm font-body font-semibold text-foreground-600 tracking-[0.3em]">
              讲座结构
            </span>
            <span className="w-8 h-px bg-accent-300/60" />
          </div>

          <h2
            className={`font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-800 mb-4 transition-all duration-1000 delay-100 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ textShadow: '0 2px 12px oklch(var(--primary-300) / 0.25)' }}
          >
            今天的路线图
          </h2>

          <p
            className={`text-base text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            从"单课评价"走向"质量治理"的过程。先看一节课怎么评，再看一批课怎么用。
          </p>
        </div>

        {/* 桌面端：7卡片横向 */}
        <div className="hidden lg:block">
          <div className="relative pb-20">
            {/* 底部时间线 */}
            <RoadLine activeStep={activeStep} />

            {/* 卡片行 */}
            <div className="flex items-start justify-center gap-3">
              {roadmapSteps.map((step, i) => {
                const isActive = activeStep >= i;
                return (
                  <div
                    key={step.id}
                    className={`relative flex flex-col items-center w-[190px] transition-all duration-600 ${
                      isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{ transitionDelay: `${i * 120}ms` }}
                  >
                    {/* 顶部图标圆 */}
                    <div
                      className={`relative z-10 w-[76px] h-[76px] rounded-full flex items-center justify-center mb-2 transition-all duration-500 ${
                        isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                      }`}
                      style={{
                        transitionDelay: `${i * 120 + 100}ms`,
                        background: 'oklch(var(--background-50) / 0.8)',
                        boxShadow: isActive
                          ? '0 6px 24px oklch(var(--primary-300) / 0.25), inset 0 0 0 2px oklch(var(--primary-300) / 0.5)'
                          : '0 4px 16px oklch(var(--primary-300) / 0.15), inset 0 0 0 1.5px oklch(var(--primary-200))',
                      }}
                    >
                      <i className={`${step.icon} text-[26px] ${isActive ? 'text-primary-600' : 'text-primary-400'}`} />
                    </div>

                    {/* 虚线连接 */}
                    <div
                      className={`w-px h-3 border-l-2 border-dashed transition-all duration-500 ${
                        isActive ? 'opacity-100' : 'opacity-0'
                      }`}
                      style={{
                        borderColor: isActive ? 'oklch(var(--primary-300) / 0.5)' : 'oklch(var(--primary-300) / 0.2)',
                        transitionDelay: `${i * 120 + 180}ms`,
                      }}
                    />

                    {/* 卡片主体 */}
                    <div
                      className={`w-full rounded-xl text-center p-4 pt-5 pb-4 transition-all duration-500 glass-liquid cursor-default hover:-translate-y-1.5 ${
                        isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                      } ${isActive ? 'border-accent-200/40' : ''}`}
                      style={{
                        transitionDelay: `${i * 120 + 200}ms`,
                        boxShadow: isActive
                          ? '0 4px 20px oklch(var(--primary-300) / 0.15), 0 0 0 1px oklch(var(--accent-200) / 0.4), inset 0 1px 0 oklch(var(--background-100) / 0.5)'
                          : '0 2px 12px oklch(var(--primary-300) / 0.08), 0 0 0 1px oklch(var(--primary-200) / 0.3)',
                      }}
                    >
                      {/* 编号 + 标签行 */}
                      <div className="flex items-center justify-center gap-2 mb-2.5">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-500 ${
                            isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                          }`}
                          style={{
                            transitionDelay: `${i * 120 + 280}ms`,
                            background: i === 0 || i === 3 || i === 6
                              ? 'oklch(var(--primary-500))'
                              : 'oklch(var(--accent-500))',
                            boxShadow: '0 2px 8px oklch(var(--primary-500) / 0.3)',
                          }}
                        >
                          <span className="text-xs font-bold text-white font-mono">{step.id}</span>
                        </div>
                        <span className="text-[10px] font-body font-medium px-2 py-0.5 rounded-full bg-accent-50 border border-accent-200/40 text-accent-600 whitespace-nowrap">
                          {step.tag}
                        </span>
                      </div>

                      {/* 标题 */}
                      <h4 className="text-[15px] font-bold font-body mb-2 text-primary-700">
                        {step.title}
                      </h4>

                      {/* 描述 */}
                      <p className="text-xs text-foreground-500 font-body leading-relaxed mb-3">
                        {step.desc}
                      </p>

                      {/* 数据条 */}
                      <div className="flex items-center justify-center gap-1.5 mb-2">
                        <i className="ri-bar-chart-line text-[10px] text-accent-400" />
                        <span className="text-[11px] font-mono font-semibold text-accent-600">
                          {step.stats}
                        </span>
                      </div>

                      {/* 底部时间标签 */}
                      <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-100/70 border border-background-200/40">
                        <i className="ri-time-line text-[9px] text-foreground-400" />
                        <span className="text-[9px] font-body text-foreground-500 whitespace-nowrap">
                          {step.time}
                        </span>
                      </div>
                    </div>

                    {/* 下方到时间线的虚线 */}
                    <div
                      className={`w-px flex-1 min-h-[28px] border-l-2 border-dashed transition-all duration-500 mt-1 ${
                        isActive ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0'
                      }`}
                      style={{
                        borderColor: isActive ? 'oklch(var(--primary-300) / 0.4)' : 'oklch(var(--primary-300) / 0.15)',
                        transformOrigin: 'top',
                        transitionDelay: `${i * 120 + 350}ms`,
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* 底部装饰 */}
            <div className="relative mt-0 h-14 mx-auto" style={{ maxWidth: '1100px' }}>
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 1100 56"
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient id="roadWave2" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="oklch(var(--primary-300))" />
                    <stop offset="50%" stopColor="oklch(var(--accent-400))" />
                    <stop offset="100%" stopColor="oklch(var(--primary-400))" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,44 C120,48 250,32 380,40 C510,48 640,26 770,36 C900,46 1000,30 1100,38"
                  stroke="url(#roadWave2)"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                  className={activeStep >= 0 ? 'opacity-100' : 'opacity-0'}
                  style={{
                    strokeDasharray: 1300,
                    strokeDashoffset: activeStep >= 6 ? 0 : 1300,
                    transition: 'stroke-dashoffset 2.5s ease-out',
                  }}
                />
                {/* 底部光带 */}
                <path
                  d="M0,44 C120,48 250,32 380,40 C510,48 640,26 770,36 C900,46 1000,30 1100,38 L1100,56 L0,56 Z"
                  fill="url(#roadWave2)"
                  opacity={activeStep >= 0 ? 0.08 : 0}
                  style={{ transition: 'opacity 1.5s ease-out 1s' }}
                />
              </svg>

              {/* 终点箭头 */}
              <div
                className={`absolute right-1 top-3 transition-all duration-700 ${
                  activeStep >= 6 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-3'
                }`}
                style={{ transitionDelay: '2s' }}
              >
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-accent-400/20 border border-accent-300/50 flex items-center justify-center">
                    <i className="ri-arrow-up-line text-lg text-accent-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 移动端 */}
        <div className="lg:hidden">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {roadmapSteps.map((step, i) => {
              const isActive = activeStep >= i;
              return (
                <div
                  key={step.id}
                  className={`flex flex-col items-center transition-all duration-600 ${
                    isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  {/* 顶部图标圆 */}
                  <div
                    className={`w-14 h-14 rounded-full flex items-center justify-center mb-2 transition-all duration-500 ${
                      isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                    }`}
                    style={{
                      transitionDelay: `${i * 100 + 80}ms`,
                      background: 'oklch(var(--background-50) / 0.8)',
                      boxShadow: isActive
                        ? '0 4px 16px oklch(var(--primary-300) / 0.2), inset 0 0 0 1.5px oklch(var(--primary-300) / 0.4)'
                        : '0 2px 10px oklch(var(--primary-300) / 0.1), inset 0 0 0 1px oklch(var(--primary-200))',
                    }}
                  >
                    <i className={`${step.icon} text-xl ${isActive ? 'text-primary-600' : 'text-primary-400'}`} />
                  </div>

                  {/* 卡片 */}
                  <div
                    className="w-full rounded-xl text-center p-3 pt-4 pb-3 glass-liquid"
                    style={{
                      boxShadow: '0 2px 12px oklch(var(--primary-300) / 0.1), 0 0 0 1px oklch(var(--primary-200) / 0.3)',
                    }}
                  >
                    <div className="flex items-center justify-center gap-1.5 mb-1.5">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center"
                        style={{
                          background: i === 0 || i === 3 || i === 6
                            ? 'oklch(var(--primary-500))'
                            : 'oklch(var(--accent-500))',
                        }}
                      >
                        <span className="text-[10px] font-bold text-white font-mono">{step.id}</span>
                      </div>
                      <span className="text-[9px] font-body font-medium px-1.5 py-0.5 rounded-full bg-accent-50 border border-accent-200/40 text-accent-600">
                        {step.tag}
                      </span>
                    </div>

                    <h4 className="text-sm font-bold font-body mb-1 text-primary-700">
                      {step.title}
                    </h4>
                    <p className="text-[11px] text-foreground-500 font-body leading-relaxed mb-2">
                      {step.desc}
                    </p>
                    <div className="flex items-center justify-center gap-1">
                      <i className="ri-bar-chart-line text-[9px] text-accent-400" />
                      <span className="text-[10px] font-mono font-semibold text-accent-600">{step.stats}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 移动端底部装饰线 */}
          <div className="mt-8 h-1.5 rounded-full bg-gradient-to-r from-primary-300/40 via-accent-400/60 to-primary-300/40" />
        </div>
      </div>
    </div>
  );
}